import React from 'react'
import PropTypes from 'prop-types'
import { noop } from 'lodash'
import { Text, Drawer, Button, Icon } from '@adc/ui-core'
import classNames from 'classnames/bind'
import {
  gtmOnboardingDrawer,
  gtmOnboardingDrawerExit,
} from '../../shared/services/tracking'
import cls from './styles.scss'
import NewOnboardingDrawer from './NewOnboardingDrawer'
const cx = classNames.bind(cls)

const baseElmId = 'onboarding_drawer'

const NextArrow = ({ onClick, className }) =>
  !className.includes('slick-disabled') && (
    <div id='next' onClick={onClick} className={cx('arrow', 'next')}>
      <Icon
        name='angle-right'
        color='black'
        iconStyle='regular'
        size='big'
        data-elm-id={`${baseElmId}_next_arrow`}
      />
    </div>
  )

const BackArrow = ({ onClick, className }) =>
  !className.includes('slick-disabled') && (
    <div id='back' onClick={onClick} className={cx('arrow')}>
      <Icon
        name='angle-left'
        color='black'
        iconStyle='regular'
        size='big'
        data-elm-id={`${baseElmId}_back_arrow`}
      />
    </div>
  )

NextArrow.propTypes = BackArrow.propTypes = {
  onClick: PropTypes.func,
  className: PropTypes.string,
}

export const DEFAULT_ONBOARDING_CONTENT = {
  content: {
    actions: {
      get_started: 'Create a Free Account Now',
      skip: 'Skip',
      start_tour: 'Take a Tour',
    },
    header: 'Welcome to Auction.com',
    sub_header1:
      'The largest online source of Foreclosure and Bank-Owned Properties not on the MLS',
    sub_header2: '',
    sub_section: {
      section1: {
        icon_label: 'Invest Smarter',
      },
      section2: {
        icon_label: 'Get Exclusive Deals',
      },
      section3: {
        icon_label: 'Bid From Anywhere',
      },
      title: "We'd love to help you:",
    },
  },
}
const DEFAULT_SIGNUP_EXPERIMENT = {
  enable: false,
  url: '',
  label: 'Get Started',
}

class OnboardingDrawer extends React.Component {
  state = {
    open: false,
  }

  static propTypes = {
    featureFlags: PropTypes.object,
    isSmartSale: PropTypes.bool,
    user: PropTypes.shape({
      logged_in: PropTypes.bool.isRequired,
    }).isRequired,
    getStarted: PropTypes.func.isRequired,
    startTour: PropTypes.func,
    elmId: PropTypes.string,
    isMobile: PropTypes.bool,
    experiments: PropTypes.object,
  }

  componentDidMount() {
    const {
      user: { logged_in: loggedIn },
    } = this.props

    const alreadyOnboarded = Boolean(
      window.localStorage.getItem('onboarding_started') || loggedIn
    )

    if (!alreadyOnboarded) {
      this.setOpen(true)
      gtmOnboardingDrawer()
      window.localStorage.setItem('onboarding_started', true)
    }
  }

  /**
   * Before taking further actions we need to wait for the Drawer to completely
   * open or close. Returned a promise with a timeOut for this reason
   */
  setOpen = open =>
    new Promise(resolve =>
      this.setState({ open }, () => setTimeout(resolve, 750))
    )

  onClose = (track = true) => {
    if (track) gtmOnboardingDrawerExit()
    return this.setOpen(false)
  }

  startTutorial = async () => {
    const { startTour } = this.props
    await this.onClose(false)
    startTour()
  }

  getStarted = async () => {
    const { getStarted = noop } = this.props
    await this.onClose(false)
    getStarted()
  }

  getContent = (
    content,
    elmId,
    startTour,
    experiment,
    enableSignUpExperiment
  ) => {
    return (
      <div
        className={cx('onboarding-drawer')}
        data-elm-id={`${elmId}_drawer_content`}
      >
        {content?.header && (
          <Text as='p' size='massive' color='success' weight='heavy'>
            {content.header}
          </Text>
        )}
        {content?.sub_header1 && (
          <Text as='p' size='huge' color='utkColorGray' weight='medium'>
            {content.sub_header1}
          </Text>
        )}
        {content?.sub_header2 && (
          <Text as='p' size='huge' color='utkColorGray' weight='medium'>
            {content.sub_header2}
          </Text>
        )}
        {content?.sub_section && (
          <Text as='p' size='big' color='utkColorGray' weight='heavy'>
            {content.sub_section.title}
          </Text>
        )}
        <div className={cx('sub-title')}>
          <div
            className={cx('sub-title-section')}
            data-elm-id={`${elmId}_title_exclusive_deals`}
          >
            <div className={cx('section-content')}>
              <div className={cx('icon-container')}>
                <div className={cx('icon-stack')}>
                  <Icon
                    name='lightbulb'
                    iconStyle='light'
                    color='gray'
                    className={cx('icon-stack-2x', 'icon')}
                  />
                  <Icon
                    name='exclamation'
                    iconStyle='solid'
                    color='adcLightGreen'
                    className={cx('icon-stack-1x', 'icon', 'exclamation')}
                  />
                </div>
              </div>
              {content?.sub_section?.section1?.icon_label && (
                <label> {content.sub_section.section1.icon_label}</label>
              )}
            </div>
          </div>
          <div
            className={cx('sub-title-section')}
            data-elm-id={`${elmId}_title_reach_your_goals`}
          >
            <div className={cx('section-content')}>
              <div className={cx('icon-container')}>
                <div className={cx('icon-stack')}>
                  <Icon
                    name='house-blank'
                    iconStyle='light'
                    color='gray'
                    className={cx('icon-stack-2x', 'icon')}
                  />
                  <Icon
                    name='dollar-sign'
                    iconStyle='solid'
                    color='adcLightGreen'
                    className={cx('icon-stack-1x', 'icon', 'dollar-sign')}
                  />
                </div>
              </div>
              {content?.sub_section?.section2?.icon_label && (
                <label> {content.sub_section.section2.icon_label}</label>
              )}
            </div>
          </div>
          <div
            className={cx('sub-title-section')}
            data-elm-id={`${elmId}_title_bid_from_anywhere`}
          >
            <div className={cx('section-content')}>
              <div className={cx('icon-container')}>
                <div className={cx('icon-stack', 'rotate')}>
                  <Icon
                    name='mobile'
                    iconStyle='light'
                    color='gray'
                    className={cx('icon-stack-2x', 'icon')}
                  />
                  <Icon
                    name='house-blank'
                    iconStyle='solid'
                    color='adcLightGreen'
                    className={cx('icon-stack-1x', 'icon', 'solid-house')}
                  />
                </div>
              </div>
              {content?.sub_section?.section3?.icon_label && (
                <label> {content.sub_section.section3.icon_label}</label>
              )}
            </div>
          </div>
        </div>
        <div className='u-mt-6'>
          {enableSignUpExperiment ? (
            this.getSignUpExperimentContent(elmId, experiment)
          ) : (
            <>
              <Button
                role='primary'
                isStretch
                onClick={this.getStarted}
                elmId={`${elmId}_get_started_button`}
              >
                {content?.actions?.get_started}
              </Button>
              {startTour && (
                <div className='u-mt-2'>
                  <Button
                    role='secondary'
                    isStretch
                    onClick={this.startTutorial}
                    elmId={`${elmId}_start_tour_button`}
                  >
                    {content?.actions?.start_tour}
                  </Button>
                </div>
              )}
              <div className='u-mt-4'>
                <a onClick={this.onClose} data-elm-id={`${elmId}_skip_button`}>
                  {content?.actions?.skip}
                </a>
              </div>
            </>
          )}
        </div>
      </div>
    )
  }

  getSignUpExperimentContent = (elmId, experiment) => {
    return (
      <>
        <Button
          role='primary'
          isStretch
          link={experiment.url}
          elmId={`${elmId}_get_started_button`}
          target='_blank'
        >
          {experiment.label}
        </Button>
        <div className='u-mt-4'>
          <a onClick={this.onClose} data-elm-id={`${elmId}_skip_button`}>
            Not Now
          </a>
        </div>
      </>
    )
  }

  // TODO: rip this out
  getOnboardingContentExperiments = (experimentsList, experiments) => {
    return experimentsList.reduce((acc, experiment) => {
      const experimentContent =
        typeof experiments?.[experiment]?.get === 'function'
          ? experiments[experiment].get(
              'onboarding_content',
              DEFAULT_ONBOARDING_CONTENT
            )
          : DEFAULT_ONBOARDING_CONTENT
      return {
        ...acc,
        ...experimentContent,
      }
    }, {})
  }

  render() {
    const {
      startTour,
      elmId = baseElmId,
      isMobile,
      experiments,
      featureFlags,
      isSmartSale,
    } = this.props

    // Flag configuration is to deliver 50% of the users to 'Control' (existing design) and 50% to 'Test' (new design)
    const shouldDisplayNewDrawer =
      isSmartSale ||
      featureFlags?.['fe.resi.ab.testing.onboarding-drawer-group']
        ?.newOnboarding

    const onboardingExperiments = [
      'ed-199-new_visitor_slider_time_to_get_started',
    ]
    const onboardingContentExperiments = this.getOnboardingContentExperiments(
      onboardingExperiments,
      experiments
    )

    // TODO: rip this out
    const signupExperiment =
      typeof experiments?.['ed203-newvisitslider_modallink']?.get === 'function'
        ? experiments['ed203-newvisitslider_modallink'].get(
            'signup-modal-link',
            DEFAULT_SIGNUP_EXPERIMENT
          )
        : DEFAULT_SIGNUP_EXPERIMENT

    const enableSignUpExperiment = isMobile && signupExperiment?.enable

    return (
      <Drawer
        open={this.state.open}
        onClose={this.onClose}
        elmId={elmId}
        headerStyle='clear'
        closeOnDimmerClick={false}
      >
        {shouldDisplayNewDrawer ? (
          <NewOnboardingDrawer
            elmId={elmId}
            getStarted={this.getStarted}
            onClose={this.onClose}
            startTutorial={startTour ? this.startTutorial : undefined}
          />
        ) : (
          this.getContent(
            onboardingContentExperiments.content,
            elmId,
            startTour,
            signupExperiment,
            enableSignUpExperiment
          )
        )}
      </Drawer>
    )
  }
}

export default OnboardingDrawer
